// The cross-tenant order monitor. GET /platform/events?topic=orders&after=
// answers the events strictly after a timestamp, oldest first (up to
// `limit`); the page polls it, carrying the last event's occurredAt back
// verbatim as the cursor (the server's microsecond precision survives), and
// shows the newest first. Events carry ids only; each restaurant's name is
// read once (GET /restaurants/{rid}) and cached.

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, label } from '../../lib/api';
import type { Restaurant } from '../restaurant/types';
import type { PlatformEvent } from './types';

const KEEP = 200;
const LOOKBACK_MS = 30 * 60_000;
const POLL_MS = 3_000;

const keyOf = (e: PlatformEvent) => `${e.restaurantId}/${e.storeId}/${e.eventSeq}`;

export function LiveOrders() {
  const [events, setEvents] = useState<PlatformEvent[]>([]);
  const [polledAt, setPolledAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const asked = useRef(new Set<string>());
  const cursor = useRef(new Date(Date.now() - LOOKBACK_MS).toISOString());

  useEffect(() => {
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function poll() {
      try {
        // drain: a full page means more may be waiting behind it
        for (;;) {
          const page = await api.get<PlatformEvent[]>('/platform/events', { topic: 'orders', after: cursor.current, limit: KEEP });
          if (stop) return;
          if (page.length > 0) {
            cursor.current = page[page.length - 1].occurredAt;
            setEvents((prev) => {
              const seen = new Set(prev.map(keyOf));
              const fresh = page.filter((e) => !seen.has(keyOf(e))).reverse();
              return [...fresh, ...prev].slice(0, KEEP);
            });
          }
          if (page.length < KEEP) break;
        }
        setPolledAt(new Date());
        setError(null);
      } catch (e) {
        if (!stop) setError(e instanceof Error ? e.message : 'The feed did not answer.');
      }
      if (!stop) timer = setTimeout(() => void poll(), POLL_MS);
    }

    void poll();
    return () => { stop = true; clearTimeout(timer); };
  }, []);

  useEffect(() => {
    for (const rid of new Set(events.map((e) => e.restaurantId))) {
      if (asked.current.has(rid)) continue;
      asked.current.add(rid);
      api.get<Restaurant>(`/restaurants/${rid}`)
        .then((r) => setNames((n) => ({ ...n, [rid]: r.name })))
        .catch(() => { /* the id stands in */ });
    }
  }, [events]);

  const live = polledAt !== null && error === null;
  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Platform</div>
          <h1>Live orders</h1>
        </div>
        <span className="chip" data-feed={live ? 'live' : 'retrying'}
          style={{ marginLeft: 'auto', '--dot': live ? 'var(--ok)' : 'var(--warn)' } as React.CSSProperties}>
          {live ? `live · ${polledAt!.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', second: '2-digit' })}` : 'connecting…'}
        </span>
      </div>
      {error && <div className="err" role="alert">{error} — retrying every {POLL_MS / 1000}s.</div>}

      <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16 }}>
        Order events across every tenant, from 30 minutes ago onward. Newest first; keeps the last {KEEP}.
      </p>

      {events.length === 0 && <div className="loading">Waiting for order events…</div>}
      {events.length > 0 && (
        <table className="tbl" data-section="events">
          <thead>
            <tr>
              <th>Time</th>
              <th>Event</th>
              <th>Restaurant</th>
              <th>Store</th>
              <th>Order</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody style={{ cursor: 'default' }}>
            {events.map((e) => (
              <tr key={keyOf(e)} data-event={e.eventType} data-entity={e.entityId ?? ''} data-restaurant={e.restaurantId}
                style={{ cursor: 'default' }}>
                <td className="tm">
                  {new Date(e.occurredAt).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', second: '2-digit' })}
                </td>
                <td><span className="src">{label(e.eventType)}</span></td>
                <td>
                  <Link to={`/r/${e.restaurantId}`} style={{ fontWeight: 700, color: 'inherit' }} title={e.restaurantId}>
                    {names[e.restaurantId] ?? e.restaurantId}
                  </Link>
                </td>
                <td className="docket" style={{ fontWeight: 500, color: 'var(--ink-2)' }}>{e.storeId}</td>
                <td>
                  {e.entityId ? (
                    <Link to={`/r/${e.restaurantId}/s/${e.storeId}/orders/${e.entityId}`} className="docket"
                      style={{ fontWeight: 500, color: 'var(--ink-2)' }}>
                      {e.entityId}
                    </Link>
                  ) : '—'}
                </td>
                <td style={{ color: 'var(--ink-3)' }}>{e.detail ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
